import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Alert, Anchor, Button, Center, Divider, Paper, PasswordInput, PinInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconBrandWindows } from '@tabler/icons-react';
import type { OpcionesAcceso, RespuestaLogin } from '@epms/shared';
import { api, mensajeDeError } from '../api.ts';
import { CLAVE_YO } from '../sesion.ts';

const ERRORES_SSO: Record<string, string> = {
  sso_sin_usuario: 'Tu cuenta de Microsoft no tiene usuario en epms. Pedile a un administrador que te dé de alta con ese email.',
  sso_otro_directorio: 'Esa cuenta de Microsoft no es del estudio.',
  sso_invitado: 'Las cuentas invitadas de otro directorio no pueden entrar: usá tu cuenta del estudio.',
  sso_otra_cuenta: 'Tu usuario ya está vinculado a otra cuenta de Microsoft.',
  sso_vencido: 'El ingreso con Microsoft tardó demasiado. Probá de nuevo.',
  sso_sin_datos: 'Microsoft no informó el email de la cuenta.',
  sso_fallido: 'No se pudo completar el ingreso con Microsoft.',
};

export function Login() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [paso, setPaso] = useState<'password' | '2fa'>('password');
  const [error, setError] = useState<string | null>(ERRORES_SSO[params.get('error') ?? ''] ?? null);
  const [enviando, setEnviando] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [conRespaldo, setConRespaldo] = useState(false);
  const [respaldo, setRespaldo] = useState('');
  const opciones = useQuery({ queryKey: ['opciones-acceso'], queryFn: () => api.get<OpcionesAcceso>('/api/auth/opciones') });
  const form = useForm({ initialValues: { email: '', password: '' } });

  async function terminar(r: RespuestaLogin) {
    if (r.estado === 'requiere_2fa') {
      setPaso('2fa');
      return;
    }
    qc.setQueryData(CLAVE_YO, r.perfil);
  }

  async function entrar(valores: { email: string; password: string }) {
    setError(null);
    setEnviando(true);
    try {
      await terminar(await api.post<RespuestaLogin>('/api/auth/login', valores));
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setEnviando(false);
    }
  }

  async function verificar(valor: string) {
    setError(null);
    setEnviando(true);
    try {
      await terminar(await api.post<RespuestaLogin>('/api/auth/2fa/verificar', { codigo: valor }));
    } catch (e) {
      setCodigo('');
      setError(mensajeDeError(e));
      // La sesión provisoria dura 5 minutos: si venció, volver a empezar.
      if (e instanceof Error && /iniciá sesión/i.test(e.message)) setPaso('password');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Center mih="100vh" p="md">
      <Paper withBorder shadow="sm" p="xl" w={380} maw="100%">
        <Stack>
          <div>
            <Title order={2}>epms</Title>
            <Text c="dimmed" size="sm">Clientes, impuestos y conciliaciones</Text>
          </div>
          {error && <Alert color="red">{error}</Alert>}
          {params.get('restablecida') && !error && <Alert color="teal">Listo: entrá con tu contraseña nueva.</Alert>}

          {paso === 'password' ? (
            <>
              <form onSubmit={form.onSubmit(entrar)}>
                <Stack>
                  <TextInput label="Email" type="email" autoComplete="username" required autoFocus {...form.getInputProps('email')} />
                  <PasswordInput label="Contraseña" autoComplete="current-password" required {...form.getInputProps('password')} />
                  <Button type="submit" loading={enviando}>Ingresar</Button>
                  {opciones.data?.recuperacion && (
                    <Anchor component={Link} to="/recuperar" size="xs" ta="center">¿Olvidaste tu contraseña?</Anchor>
                  )}
                </Stack>
              </form>
              {opciones.data?.microsoft && (
                <>
                  <Divider label="o" />
                  <Button component="a" href="/api/auth/microsoft/iniciar" variant="default" leftSection={<IconBrandWindows size={18} />}>
                    Ingresar con Microsoft
                  </Button>
                </>
              )}
            </>
          ) : (
            conRespaldo ? (
              <form onSubmit={(e) => { e.preventDefault(); void verificar(respaldo.trim().replace(/\s+/g, '-')); }}>
                <Stack>
                  <Text size="sm">Ingresá uno de tus códigos de respaldo (cada uno sirve una sola vez).</Text>
                  <TextInput placeholder="xxxxx-xxxxx" autoFocus autoComplete="one-time-code" value={respaldo} onChange={(e) => setRespaldo(e.currentTarget.value)} />
                  <Button type="submit" loading={enviando} disabled={respaldo.replace(/[\s-]/g, '').length !== 10}>Ingresar</Button>
                  <Button variant="subtle" size="xs" onClick={() => setConRespaldo(false)}>Usar la app de autenticación</Button>
                </Stack>
              </form>
            ) : (
            <Stack align="center">
              <Text size="sm" ta="center">Ingresá el código de 6 dígitos de tu app de autenticación.</Text>
              <PinInput
                length={6}
                type="number"
                oneTimeCode
                autoFocus
                value={codigo}
                onChange={setCodigo}
                onComplete={(v) => void verificar(v)}
                disabled={enviando}
              />
              <Button variant="subtle" size="xs" onClick={() => setConRespaldo(true)}>Perdí el teléfono: usar un código de respaldo</Button>
              <Button variant="subtle" size="xs" onClick={() => { setPaso('password'); setError(null); }}>Volver</Button>
            </Stack>
            )
          )}
        </Stack>
      </Paper>
    </Center>
  );
}
